import { MENU_PRODUCTS } from '../data/menuData'

export type Station = 'caja' | 'cocina' | 'pizzeria' | 'bebidas'

export interface StationDef {
  id: Station
  label: string
  icon: string
  color: string
  bg: string
  border: string
  description: string
}

export const STATIONS: StationDef[] = [
  {
    id: 'caja',
    label: 'Caja',
    icon: '💰',
    color: 'text-[#F2C94C]',
    bg: 'bg-[#F2C94C]/10',
    border: 'border-[#F2C94C]/20',
    description: 'Vista completa — pedidos, pagos, reportes',
  },
  {
    id: 'cocina',
    label: 'Cocina',
    icon: '🔥',
    color: 'text-[#00F5C9]',
    bg: 'bg-[#00F5C9]/10',
    border: 'border-[#00F5C9]/20',
    description: 'Sándwiches, wraps, cremas, bowls, niños',
  },
  {
    id: 'pizzeria',
    label: 'Pizzería',
    icon: '🍕',
    color: 'text-[#7A5CFF]',
    bg: 'bg-[#7A5CFF]/10',
    border: 'border-[#7A5CFF]/20',
    description: 'Pizzas y productos de pizzería',
  },
  {
    id: 'bebidas',
    label: 'Bebidas',
    icon: '🥤',
    color: 'text-[#00F5C9]',
    bg: 'bg-[#00F5C9]/10',
    border: 'border-[#00F5C9]/20',
    description: 'Bebidas, smoothies, batidos',
  },
]

// Map product types/categories to stations
const COCINA_TYPES = new Set([
  'sandwich',
  'wrap',
  'bowl',
  'crema',
  'snack_bowl',
  'ninos',
])
const BEBIDAS_TYPES = new Set(['bebida'])
const PIZZERIA_TYPES = new Set(['pizza']) // Ready for when pizza products are added

const COCINA_CATEGORIES = new Set([
  'Sándwich',
  'Wrap',
  'Cremas',
  'Snack Bowl',
  'Niños',
])
const BEBIDAS_CATEGORIES = new Set(['Bebidas'])
const PIZZERIA_CATEGORIES = new Set(['Pizzería', 'Pizza']) // Ready for future

// Build a lookup: product name → station(s)
const productStationMap = new Map<string, Station[]>()

MENU_PRODUCTS.forEach((product) => {
  const stations: Station[] = []
  if (
    COCINA_TYPES.has(product.type || '') ||
    COCINA_CATEGORIES.has(product.category)
  ) {
    stations.push('cocina')
  }
  if (
    BEBIDAS_TYPES.has(product.type || '') ||
    BEBIDAS_CATEGORIES.has(product.category)
  ) {
    stations.push('bebidas')
  }
  if (
    PIZZERIA_TYPES.has(product.type || '') ||
    PIZZERIA_CATEGORIES.has(product.category)
  ) {
    stations.push('pizzeria')
  }
  if (stations.length > 0) {
    productStationMap.set(product.name.toLowerCase(), stations)
  }
})

// Parse order_text to extract item lines
export function parseOrderItems(orderText: string): string[] {
  if (!orderText) return []
  const lines = orderText.split('\n')
  // Item lines match pattern: "Product name xN — ₡X,XXX"
  return lines.filter(
    (line) => /x\d+\s*—\s*₡/.test(line) || /x\d+\s*—\s*\$/.test(line),
  )
}

// Check if an item line belongs to a station
export function itemBelongsToStation(
  itemLine: string,
  station: Station,
): boolean {
  if (station === 'caja') return true

  const normalizedLine = itemLine.toLowerCase().trim()

  for (const [productName, stations] of productStationMap.entries()) {
    if (normalizedLine.includes(productName) && stations.includes(station)) {
      return true
    }
  }

  // Fallback heuristics for items not in MENU_PRODUCTS (e.g., custom bowls)
  if (station === 'cocina') {
    const cocinaKeywords = [
      'sándwich',
      'sandwich',
      'wrap',
      'crema',
      'bowl',
      'pollo',
      'niño',
      'dedos',
    ]
    return cocinaKeywords.some((kw) => normalizedLine.includes(kw))
  }
  if (station === 'bebidas') {
    const bebidasKeywords = [
      'natural',
      'smoothie',
      'coca',
      'fanta',
      'sprite',
      'agua',
      'chocolate',
      'aguadulce',
      'tropical',
      'leche',
      'batido',
    ]
    return bebidasKeywords.some((kw) => normalizedLine.includes(kw))
  }
  if (station === 'pizzeria') {
    const pizzaKeywords = ['pizza', 'pizz']
    return pizzaKeywords.some((kw) => normalizedLine.includes(kw))
  }

  return false
}

// Check if an order has items for a specific station
export function orderHasItemsForStation(
  orderText: string,
  station: Station,
): boolean {
  if (station === 'caja') return true
  const items = parseOrderItems(orderText)
  return items.some((item) => itemBelongsToStation(item, station))
}

// Get only the items relevant to a station from an order
export function getStationItems(orderText: string, station: Station): string[] {
  if (station === 'caja') return parseOrderItems(orderText)
  const items = parseOrderItems(orderText)
  return items.filter((item) => itemBelongsToStation(item, station))
}

// Count items for a station across all orders
export function countStationItems(
  orders: { order_text: string; status: string }[],
  station: Station,
): number {
  const activeStatuses = new Set(['new', 'accepted', 'preparing', 'ready'])
  return orders.filter(
    (o) =>
      activeStatuses.has(o.status) &&
      orderHasItemsForStation(o.order_text, station),
  ).length
}
